<template>
    <div id="manage-add-admin" class="flex justify-center items-center w-full h-full p-4">
        <div @click="$emit('close')" class="manage-quiz-backdrop absolute pin-t pin-l w-full h-full"></div>
        <div class="manage-content flex flex-col w-full h-full md:w-auto bg-white rounded-lg">
            <div class="manage-header flex justify-between items-center p-4 border-b">
                <h1 class="m-0 font-size-18">{{ $t('manage_school_settings_assign_admin_header') }}</h1>
                <div @click="$emit('close')" class="manage-close-button flex justify-center items-center">
                    <i class="fas fa-times text-xl shadow-text-none cursor-pointer"></i>
                </div>
            </div>
            <div class="manage-body flex-1 flex flex-col p-4 overflow-hidden">
                <ManageSearch @search="onSearch($event)" :disabled-search="disableAll || loading" />
                <ManageTable
                    @pagination-change="onPageChange($event)"
                    :list="users"
                    :current-page="currentPage"
                    :page-count="pageCount"
                    :items-per-page="itemsPerPage"
                    :total-items="totalItems"
                    :loading="loading"
                    :disable-pagination="disableAll"
                >
                    <template #header>
                        <h3 class="w-4/5 m-0 font-size-16">
                            {{ $t('manage_school_table_header_name') }}
                        </h3>
                        <h3 class="w-1/5 m-0 font-size-16 text-right">
                            {{ $t('manage_school_settings_assign_admin_table_header_actions') }}
                        </h3>
                    </template>
                    <template v-slot="{ item }">
                        <div class="user-detail flex items-center w-full xl:w-4/5">
                            <div class="avatar flex justify-center items-center rounded-full overflow-hidden">
                                <img
                                    v-if="item.image_profile"
                                    :src="item.image_profile"
                                    class="w-full h-full object-cover"
                                />
                                <i v-else class="fas fa-user text-white"></i>
                            </div>
                            <div class="flex-1 overflow-hidden">
                                <div class="name flex items-center text-sm">
                                    <span class="truncate">{{ item.name }}</span>
                                </div>

                                <div class="role text-xs truncate">
                                    {{ item.email }}
                                </div>
                            </div>
                        </div>
                        <div class="settings flex justify-end w-full xl:w-1/5">
                            <div class="flex w-full xl:w-auto">
                                <XButton
                                    v-tippy="{ placement: 'top' }"
                                    @click="assignAdmin(item)"
                                    :content="$t('manage_school_settings_button_assign_admin')"
                                    :class="`assign-admin-button flex-1 ml-auto shadow-text-none`"
                                    :disabled="item.assignLoading || disableAll || item.assigned"
                                    bg-color="#28a745"
                                >
                                    <span v-if="item.assignLoading" class="close-loader"></span>
                                    <template v-else>
                                        <i v-if="item.assigned" class="fas fa-check"></i>
                                        <i v-else class="fas fa-plus"></i>
                                    </template>
                                </XButton>
                            </div>
                        </div>
                    </template>

                    <template #empty>
                        <div class="flex justify-center items-center w-full h-full p-4">
                            <div class="flex flex-col justify-center items-center max-w-xl">
                                <i class="fas fa-street-view"></i>
                                <h4
                                    v-html="$t('manage_school_settings_assign_admin_empty_state')"
                                    class="text-center m-0 mt-4"
                                ></h4>
                            </div>
                        </div>
                    </template>
                    <template #loading>
                        <InnerLoader color="#333" class="pin-t pin-l" />
                    </template>
                </ManageTable>
            </div>
        </div>
    </div>
</template>

<script>
import ManageSearch from '@/components/manage-school/ManageSearch'
import ManageTable from '@/components/manage-school/ManageTable'
import XButton from '@/components/v2/XButton'
import InnerLoader from '@/components/InnerLoader'
import { load } from 'recaptcha-v3'

export default {
    components: {
        ManageSearch,
        ManageTable,
        XButton,
        InnerLoader
    },

    data() {
        return {
            addedUsers: [],
            currentPage: 1,
            pageCount: 0,
            itemsPerPage: 0,
            totalItems: 0,
            keyword: '',
            users: null,
            loading: false,
            disableAll: false
        }
    },

    created() {
        this.fetchSchoolUser()
    },

    methods: {
        onSearch(keyword) {
            if (this.keyword === keyword) return

            this.fetchSchoolUser(keyword)
        },

        async fetchSchoolUser(keyword) {
            if (this.keyword === keyword) return

            this.loading = true
            this.disableAll = true

            this.users = null

            const urlParams = new URL(window.location.href)

            if (typeof keyword !== 'undefined') {
                this.keyword = keyword
                this.currentPage = 1

                urlParams.searchParams.delete('page')

                window.history.replaceState(null, null, urlParams.toString())
            }

            try {
                const res = await this.$store.dispatch('getUserInSchool', {
                    params: {
                        is_school_admin: 0,
                        keyword: this.keyword,
                        page: this.currentPage
                    }
                })

                this.users = res.data.data.map(user => {
                    return {
                        ...user,
                        assignLoading: false,
                        assigned: false
                    }
                })

                this.currentPage = res.data.current_page
                this.pageCount = res.data.page_count
                this.itemsPerPage = res.data.perpage
                this.totalItems = res.data.total
            } catch {
                this.toastError(this.$t('manage_school_popup_error'))
            } finally {
                this.loading = false
                this.disableAll = false
            }
        },

        onPageChange(value) {
            this.currentPage = value
            this.fetchSchoolUser()
        },

        async assignAdmin(user) {
            if (this.disableAll || user.assignLoading || user.assigned) return

            this.disableAll = true
            user.assignLoading = true

            try {
                const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                    useRecaptchaNet: true,
                    autoHideBadge: true
                })

                const token = await recaptcha.execute('validate_captcha', {
                    action: 'validate_captcha'
                })

                await this.$store.dispatch('updateSchoolAdminStatus', {
                    user_id: user._id.$oid,
                    status: 'assign',
                    captcha_response: token
                })

                user.assignLoading = false
                user.assigned = true

                this.toastSuccess(this.$t('manage_school_users_popup_assign_admin_complete'))

                this.$emit('assigned')
            } catch {
                this.toastError(this.$t('manage_school_popup_error'))
                user.assignLoading = false
            } finally {
                this.disableAll = false
            }
        }
    }
}
</script>

<style lang="scss">
#manage-add-admin {
    color: #333;
    z-index: 100;

    .manage-quiz-backdrop {
        background-color: rgba(0, 0, 0, 0.3);
        z-index: -1;
    }

    .manage-content {
        @media screen and (min-width: 768px) {
            max-width: 700px;
            min-width: 700px;
        }

        .manage-header {
            border-bottom: 1px solid #ced4da;

            .manage-close-button {
                width: 1.5rem;
                height: 1.5rem;
            }
        }

        .manage-body {
            row-gap: 1rem;

            .manage-table {
                .manage-table-item {
                    row-gap: 1rem;

                    .user-detail {
                        column-gap: 1rem;

                        .avatar {
                            width: 2rem;
                            height: auto !important;
                            aspect-ratio: 1 / 1;
                            background-color: gray;
                        }
                    }

                    .settings {
                        > div {
                            column-gap: 0.5rem;

                            button {
                                width: 100%;
                                height: 2rem;

                                @media screen and (min-width: 1200px) {
                                    width: 2rem;
                                    height: auto;
                                    padding: 0;
                                    aspect-ratio: 1 / 1;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
